import useApi from "../services/ApiService";

export type ServiceVersion = {
  id?: string;
  version: string;
  blobName: string;
  tag: string;
  isHidden: boolean;
  failedClients: number;
  isBlobMissing: boolean;
}

export type LatestPublicVersion = {
  version: string;
  blobName: string;
}

export class ServiceVersionRepository {
  private api = useApi();

  async getDownloadUrl(blobName: string): Promise<string> {
    try {
      const response = await this.api.get(`/api/updates/blob-url?blobName=${encodeURIComponent(blobName)}`);
      return response.data;
    } catch (error: any) {
      // Handle error
      if (error?.response?.status === 404)
      {
          throw new Error("The build for the selected version does not exist. The selected version will be marked as missing.");
      }

      throw new Error("Failed to fetch download URL");
    }
  }

  /** The build an updater, or a client with no Service Version, installs; `null` when no public build exists. */
  async getLatestPublicVersion(appType: "client" | "updater"): Promise<LatestPublicVersion | null> {
    try {
      const applicationType = appType === "client" ? "Client" : "Updater";
      const response = await this.api.get<LatestPublicVersion>(`/api/updates/versions/latest?applicationType=${applicationType}`);
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      throw new Error("Failed to fetch the latest public version");
    }
  }

  async changeVisibility(id: string, isHidden: boolean): Promise<void> {
    try {
      await this.api.post(`/api/updates/versions/change-visibility`, { id, isHidden });
    } catch (error) {
      // Handle error
      throw new Error("Failed to change visibility");
    }
  }

  /** Whether the build file is in storage now; the version's missing flag is cleared only when it is. */
  async recheckMissingBuild(id: string): Promise<boolean> {
    try {
      const response = await this.api.post<{ found: boolean }>(`/api/updates/versions/recheck-missing-build`, { id });
      return response.data.found;
    } catch (error) {
      // Handle error
      throw new Error("Failed to reset missing build");
    }
  }

  async createServiceVersion(
    version: string,
    blobName: string,
    tag: string,
    isHidden: boolean
  ): Promise<ServiceVersion> {
    try {
      const response = await this.api.post("/api/updates/versions/create", {
        version,
        blobName,
        applicationType: "client",
        tag,
        isHidden
      });
      return response.data as ServiceVersion;
    } catch (error) {
      // Handle error
      throw new Error("Failed to create service version");
    }
  }

  async createUpdaterVersion(
    version: string,
    blobName: string,
    tag: string,
    isHidden: boolean
  ): Promise<ServiceVersion> {
    try {
      const response = await this.api.post("/api/updates/versions/create", {
        version,
        blobName,
        applicationType: "updater",
        tag,
        isHidden
      });
      return response.data as ServiceVersion;
    } catch (error) {
      // Handle error
      throw new Error("Failed to create service version");
    }
  }

  async getServiceVersions(): Promise<ServiceVersion[]> {
    try {
      const response = await this.api.get("/api/updates/versions/client");
      return response.data as ServiceVersion[];
    } catch (error) {
      // Handle error
      throw new Error("Failed to fetch service versions");
    }
  }

  async getAllKnownClientVersions(): Promise<ServiceVersion[]> {
    try {
      const response = await this.api.get("/api/updates/versions/all-known?applicationType=client");
      const clientAppSetups = response.data as ServiceVersion[];
      return clientAppSetups;
    } catch (error) {
      // Handle error
      throw new Error("Failed to fetch service versions");
    }
  }

  async getAllKnownUpdaterVersions(): Promise<ServiceVersion[]> {
    try {
      const response = await this.api.get("/api/updates/versions/all-known?applicationType=updater");
      const clientAppSetups = response.data as ServiceVersion[];
      return clientAppSetups;
    } catch (error) {
      // Handle error
      throw new Error("Failed to fetch service versions");
    }
  }

  async discoverClientVersions(): Promise<ServiceVersion[]> {
    try {
      const response = await this.api.get("/api/updates/versions/discover?applicationType=client");
      const clientAppSetups = response.data as ServiceVersion[];
      return clientAppSetups;
    } catch (error) {
      // Handle error
      throw new Error("Failed to fetch service versions");
    }
  }

  async discoverUpdaterVersions(): Promise<ServiceVersion[]> {
    try {
      const response = await this.api.get("/api/updates/versions/discover?applicationType=updater");
      const clientAppSetups = response.data as ServiceVersion[];
      return clientAppSetups;
    } catch (error) {
      // Handle error
      throw new Error("Failed to fetch service versions");
    }
  }
}

export default ServiceVersionRepository;
